import { useEffect, useRef, useState } from 'react';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import { smartApi, type HoldScope, type LegalHoldView, type SmartSettings, type SmartSettingsView } from '../../api/client';
import { dateOnly, plural } from '../../lib/format';
import { useData } from '../../lib/useData';
import { Button, Card, Chip, Field, LoadError, Loading, Notice, SelectField } from '../ui';

/**
 * Company Settings → Messages: the smart messaging rules (retention, archiving quiet channels,
 * closing quiet text conversations, the spam filter), and legal holds. Company admins and Stitch
 * staff; only Stitch staff lift a hold.
 */

const errorText = (e: unknown) => (e instanceof Error ? e.message : 'That didn’t save.');

const KEEP = [
  { value: '', label: 'Forever' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '6 months' },
  { value: '365', label: '1 year' },
  { value: '730', label: '2 years' },
  { value: '1095', label: '3 years' },
  { value: '2555', label: '7 years' }
];
const QUIET_CHANNEL = [7, 14, 30, 60, 90, 180, 365];
const QUIET_TEXT = [1, 2, 3, 7, 14, 30];

/** The choices, with a saved value that isn't one of them kept as its own. */
function withSaved(list: { value: string; label: string }[], saved: number | null) {
  return saved === null || list.some((o) => o.value === String(saved)) ? list : [...list, { value: String(saved), label: plural(saved, 'day') }].sort((a, b) => Number(a.value || 1e9) - Number(b.value || 1e9));
}
const dayOptions = (days: number[], saved: number | null) => withSaved(days.map((d) => ({ value: String(d), label: plural(d, 'day') })), saved);

interface Form {
  enabled: boolean;
  chat: string;
  texts: string;
  archive: boolean;
  archiveDays: string;
  close: boolean;
  closeDays: string;
  spam: boolean;
  ai: boolean;
  /** File retention (2936): '30', '60' or '90'. */
  files: string;
}
/** How long files in Messages are kept (2936): 90 days is the most. */
const FILE_KEEP = [
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' }
];
const formOf = (s: SmartSettings): Form => ({
  enabled: s.enabled,
  chat: s.chatDays === null ? '' : String(s.chatDays),
  texts: s.textDays === null ? '' : String(s.textDays),
  archive: s.archiveDays !== null,
  archiveDays: String(s.archiveDays ?? 30),
  close: s.closeDays !== null,
  closeDays: String(s.closeDays ?? 7),
  spam: s.spamFilter,
  ai: s.aiCatchup,
  files: String(s.fileDays ?? 90)
});

export function SmartMessagesCard({ domain, companyName }: { domain: string; companyName: string }) {
  // Stitch's own settings, not the phone system's: never reloaded under someone choosing.
  const data = useData(() => smartApi.settings(domain), [domain], { live: false });
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const filled = useRef<string | null>(null);
  const v: SmartSettingsView | undefined = data.data;
  useEffect(() => {
    if (v && filled.current !== domain) {
      filled.current = domain;
      setForm(formOf(v.settings));
    }
  }, [v, domain]);

  if (data.error)
    return (
      <Card title="Messages">
        <LoadError error={data.error} retry={data.reload} />
      </Card>
    );
  if (!v || !form)
    return (
      <Card title="Messages">
        <Loading label="Loading Messages settings" />
      </Card>
    );

  const s = v.settings;
  const set = (patch: Partial<Form>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const next = {
    enabled: form.enabled,
    chatDays: form.chat ? Number(form.chat) : null,
    textDays: form.texts ? Number(form.texts) : null,
    archiveDays: form.archive ? Number(form.archiveDays) : null,
    closeDays: form.close ? Number(form.closeDays) : null,
    spamFilter: form.spam,
    aiCatchup: form.ai,
    fileDays: Number(form.files) as 30 | 60 | 90
  };
  const changed = (Object.keys(next) as (keyof typeof next)[]).some((k) => next[k] !== s[k]);
  const deletes = (next.chatDays !== null && (s.chatDays === null || next.chatDays < s.chatDays)) || (next.textDays !== null && (s.textDays === null || next.textDays < s.textDays));
  const fewerFileDays = next.fileDays < (s.fileDays ?? 90);
  const filesStart = v.files?.startsAt && new Date(v.files.startsAt).getTime() > Date.now() ? v.files.startsAt : null;
  const startsLater = s.retentionFrom && new Date(s.retentionFrom).getTime() > Date.now() && (s.chatDays !== null || s.textDays !== null);

  async function save() {
    setBusy(true);
    setError('');
    setSaved('');
    try {
      const r = await smartApi.saveSettings(domain, next);
      setForm(formOf(r.settings));
      data.reload();
      const from = r.settings.retentionFrom && new Date(r.settings.retentionFrom).getTime() > Date.now() ? ` Nothing is deleted before ${dateOnly(r.settings.retentionFrom)}, so there’s time to change your mind.` : '';
      setSaved(
        r.settings.enabled
          ? `Saved.${deletes || fewerFileDays ? from : ''}`
          : `Saved. Smart messaging is off: Stitch leaves your messages as they are (files are still kept ${r.settings.fileDays} days).`
      );
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Messages" action={<Chip small tone={s.enabled ? 'ok' : 'neutral'}>{s.enabled ? 'Smart messaging on' : 'Off'}</Chip>}>
      <div className="card-body settings-section smart-settings">
        <p className="muted settings-lede">
          Rules Stitch follows in the background for {companyName}’s channels, direct messages and texts. It never writes or sends anything for anyone, and nothing here
          slows Messages down.
        </p>
        {!v.editable && <Notice tone="warn">This tenant isn’t a Stitch customer yet, so it has nowhere to keep these settings. Stitch staff can add it under Customers.</Notice>}
        {v.editable && !v.service.running && (
          <Notice tone="neutral">Stitch’s smart messaging service isn’t running on this portal right now. Your settings are kept, and take effect once it is.</Notice>
        )}

        <label className="check-row">
          <input type="checkbox" checked={form.enabled} disabled={!v.editable} onChange={(e) => set({ enabled: e.target.checked })} />
          <span className="cell-stack">
            <strong>Smart messaging at {companyName}</strong>
            <small className="muted">Off: nothing is archived, closed or flagged, and no messages are deleted automatically. Files are still kept at most 90 days (below).</small>
          </span>
        </label>

        <fieldset className="plain-fieldset smart-group" disabled={!v.editable || !form.enabled}>
          <legend>Keep messages</legend>
          <div className="form-grid">
            <SelectField label="Chat messages (channels and direct messages)" value={form.chat} onChange={(e) => set({ chat: e.target.value })} options={withSaved(KEEP, s.chatDays)} />
            <SelectField label="Texts, with their pictures" value={form.texts} onChange={(e) => set({ texts: e.target.value })} options={withSaved(KEEP, s.textDays)} />
          </div>
          <p className="muted small-print">
            Older ones are deleted for good, a few at a time, and Activity shows how many. Nothing under a legal hold is ever deleted. A new or shorter rule starts a day
            after you save it, so a mistake can still be undone.
          </p>
          {deletes && changed && <Notice tone="warn">Saving this deletes messages older than you chose, starting tomorrow. Deleted messages can’t be brought back.</Notice>}
          {startsLater && !changed && <Notice tone="neutral">Deleting starts on {dateOnly(s.retentionFrom!)}.</Notice>}
          {v.holds > 0 && (
            <Notice tone="neutral" icon="lock">
              {v.holds === 1 ? 'A legal hold is on' : `${v.holds} legal holds are on`}: nothing it covers is deleted.
            </Notice>
          )}
        </fieldset>

        <fieldset className="plain-fieldset smart-group" disabled={!v.editable}>
          <legend>Files</legend>
          <div className="form-grid">
            <SelectField label="Keep files in channels and direct messages" value={form.files} onChange={(e) => set({ files: e.target.value })} options={FILE_KEEP} />
          </div>
          <p className="muted small-print">
            Files are kept from when they were posted, 90 days at the most. Older ones are removed for good at night; their message stays and says “File removed”. A file
            is copied into your archive before it goes, and nothing under a legal hold is removed. To keep files longer, keep your own copy (Settings → Storage and
            retention). A shorter time starts a day after you save it.
          </p>
          {fewerFileDays && changed && <Notice tone="warn">{`Saving this removes files older than ${next.fileDays} days, starting tomorrow night. Removed files can’t be brought back.`}</Notice>}
          {filesStart && (
            <Notice tone="neutral">{`Stitch starts removing files older than ${s.fileDays} days on ${dateOnly(filesStart)}${v.files?.older ? `: ${v.files.older === 1 ? '1 file is' : `${v.files.older} files are`} older now` : ''}.`}</Notice>
          )}
        </fieldset>

        <fieldset className="plain-fieldset smart-group" disabled={!v.editable || !form.enabled}>
          <legend>Quiet channels</legend>
          <label className="check-row">
            <input type="checkbox" checked={form.archive} onChange={(e) => set({ archive: e.target.checked })} />
            <span className="cell-stack">
              <strong>Archive channels nobody posts in</strong>
              <small className="muted">
                Three days before, a note in the channel says when. Archived channels stay readable and searchable, and an admin unarchives one in a click. #general is
                never archived.
              </small>
            </span>
          </label>
          {form.archive && (
            <div className="smart-days">
              <SelectField label="After no new messages for" value={form.archiveDays} onChange={(e) => set({ archiveDays: e.target.value })} options={dayOptions(QUIET_CHANNEL, s.archiveDays)} />
            </div>
          )}
        </fieldset>

        <fieldset className="plain-fieldset smart-group" disabled={!v.editable || !form.enabled}>
          <legend>Text conversations</legend>
          <label className="check-row">
            <input type="checkbox" checked={form.close} onChange={(e) => set({ close: e.target.checked })} />
            <span className="cell-stack">
              <strong>Close text conversations that go quiet</strong>
              <small className="muted">A closed conversation opens again by itself as soon as there’s a new text.</small>
            </span>
          </label>
          {form.close && (
            <div className="smart-days">
              <SelectField label="After no new texts for" value={form.closeDays} onChange={(e) => set({ closeDays: e.target.value })} options={dayOptions(QUIET_TEXT, s.closeDays)} />
            </div>
          )}
        </fieldset>

        <fieldset className="plain-fieldset smart-group" disabled={!v.editable || !form.enabled}>
          <legend>Spam filter</legend>
          <label className="check-row">
            <input type="checkbox" checked={form.spam} onChange={(e) => set({ spam: e.target.checked })} />
            <span className="cell-stack">
              <strong>Move likely spam and robotexts to Spam</strong>
              <small className="muted">
                Rules spot scam wording, shortened links and the same text from many numbers. Nothing is deleted, and Not spam puts a conversation back in one click. Replies
                like STOP or START, and numbers your company has texted, always reach your inbox.
              </small>
            </span>
          </label>
        </fieldset>

        <fieldset className="plain-fieldset smart-group" disabled={!v.editable || !form.enabled || !v.ai?.available}>
          <legend>Catch me up (AI)</legend>
          <label className="check-row">
            <input type="checkbox" checked={form.ai} onChange={(e) => set({ ai: e.target.checked })} />
            <span className="cell-stack">
              <strong>Let people ask for a catch-up on what they haven’t read</strong>
              <small className="muted">
                A short summary written by AI, for the person who asks only, of messages they can already see. It also turns on the summary beside texts shared by a ring
                group or call queue (where its Texting rules allow), which everyone on that team sees. To write them, those messages are sent to Anthropic’s Claude API.
                On unless you switch it off.
              </small>
            </span>
          </label>
          {v.editable && !v.ai?.available && <Notice tone="neutral">Catch-ups aren’t set up on this portal yet. Ask Stitch support.</Notice>}
        </fieldset>

        {saved && <Notice tone="ok">{saved}</Notice>}
        {error && <Notice tone="crit">{error}</Notice>}
        <div className="settings-actions">
          <Button variant="primary" disabled={busy || !changed || !v.editable} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save Messages settings'}
          </Button>
        </div>
        {s.updatedAt && (
          <p className="muted small-print">
            Last changed by {s.updatedBy || 'someone'} on {dateOnly(s.updatedAt)}.
          </p>
        )}
      </div>
    </Card>
  );
}

/** What a hold covers, in plain words. */
function holdTarget(h: LegalHoldView, company: string): string {
  const days =
    h.fromDay && h.toDay ? `, ${dayOnly(h.fromDay)} to ${dayOnly(h.toDay)}` : h.fromDay ? `, from ${dayOnly(h.fromDay)}` : h.toDay ? `, up to ${dayOnly(h.toDay)}` : '';
  return holdWhat(h, company) + days;
}

/** A company-local day (YYYY-MM-DD) as words. */
const dayOnly = (d: string) => dateOnly(`${d}T12:00:00Z`);

function holdWhat(h: LegalHoldView, company: string): string {
  if (h.scope === 'number') return `Calls, voicemail and texts with ${formatPhoneNumber(h.number ?? '')}`;
  if (h.scope === 'queue') return `${h.target?.name ?? `Ring group or queue ${h.extension ?? ''}`}: its calls, recordings and voicemail, and texts that go to it`;
  if (h.scope === 'item') return h.target?.name ?? 'One item';
  if (h.scope === 'company') return `Everything at ${company}`;
  if (h.scope === 'person') return `${h.target?.name ?? 'Someone'}: what they wrote, and the conversations they’re in`;
  if (h.target?.kind === 'text' && h.target.number) return `Texts with ${formatPhoneNumber(h.target.number)}`;
  return h.target?.name ?? 'A conversation';
}

export function LegalHoldsCard({ domain, companyName }: { domain: string; companyName: string }) {
  const data = useData(() => smartApi.holds(domain), [domain], { live: false });
  const [placing, setPlacing] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const d = data.data;
  if (data.error)
    return (
      <Card title="Legal holds">
        <LoadError error={data.error} retry={data.reload} />
      </Card>
    );
  if (!d)
    return (
      <Card title="Legal holds">
        <Loading label="Loading legal holds" />
      </Card>
    );
  const on = d.holds.filter((h) => !h.liftedAt);
  const lifted = d.holds.filter((h) => h.liftedAt).slice(0, 10);

  async function lift(h: LegalHoldView) {
    setBusy(h.id);
    setError('');
    try {
      await smartApi.liftHold(domain, h.id);
      data.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy('');
    }
  }

  return (
    <Card title="Legal holds" action={<Chip small tone={on.length ? 'warn' : 'neutral'}>{on.length ? `${on.length} on` : 'None'}</Chip>}>
      <div className="card-body settings-section">
        <p className="muted settings-lede">
          While a legal hold is on, nothing it covers is deleted, whatever the rules above or how long you keep things (Storage and retention) say: not by Stitch, and not
          by anyone deleting a message. {companyName}’s admins and Stitch support can place one; only Stitch support can lift it, so ask them when the matter is over.
        </p>
        {on.length > 0 ? (
          <ul className="smart-holds">
            {on.map((h) => (
              <li key={h.id}>
                <span className="cell-stack">
                  <strong>{holdTarget(h, companyName)}</strong>
                  <small className="muted">
                    For {h.reason} · placed by {h.placedByName || 'someone'} on {dateOnly(h.placedAt)}
                  </small>
                </span>
                {d.canLift && (
                  <Button size="small" variant="ghost" disabled={busy !== ''} onClick={() => void lift(h)}>
                    {busy === h.id ? 'Lifting…' : 'Lift'}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No legal holds are on.</p>
        )}
        {lifted.length > 0 && (
          <details className="smart-lifted">
            <summary>Lifted holds ({lifted.length})</summary>
            <ul className="smart-holds">
              {lifted.map((h) => (
                <li key={h.id}>
                  <span className="cell-stack">
                    <span>{holdTarget(h, companyName)}</span>
                    <small className="muted">
                      For {h.reason} · placed {dateOnly(h.placedAt)}, lifted by {h.liftedByName || 'Stitch'} on {dateOnly(h.liftedAt!)}
                    </small>
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
        {error && <Notice tone="crit">{error}</Notice>}
        {d.canPlace && !placing && (
          <div className="settings-actions">
            <Button icon="lock" onClick={() => setPlacing(true)}>
              Place a legal hold
            </Button>
          </div>
        )}
        {placing && (
          <PlaceHold
            domain={domain}
            companyName={companyName}
            onCancel={() => setPlacing(false)}
            onPlaced={() => {
              setPlacing(false);
              data.reload();
            }}
          />
        )}
      </div>
    </Card>
  );
}

function PlaceHold({ domain, companyName, onCancel, onPlaced }: { domain: string; companyName: string; onCancel: () => void; onPlaced: () => void }) {
  const targets = useData(() => smartApi.holdTargets(domain), [domain], { live: false });
  const [scope, setScope] = useState<HoldScope>('company');
  const [person, setPerson] = useState('');
  const [conversation, setConversation] = useState('');
  const [number, setNumber] = useState('');
  const [team, setTeam] = useState('');
  const [fromDay, setFromDay] = useState('');
  const [toDay, setToDay] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const t = targets.data;
  const ready =
    reason.trim() !== '' &&
    (scope === 'company' || (scope === 'person' && person) || (scope === 'conversation' && conversation) || (scope === 'number' && number.trim()) || (scope === 'queue' && team)) &&
    (!fromDay || !toDay || fromDay <= toDay);

  async function place() {
    setBusy(true);
    setError('');
    try {
      const why = reason.trim();
      const days = { ...(fromDay ? { fromDay } : {}), ...(toDay ? { toDay } : {}) };
      await smartApi.placeHold(
        domain,
        scope === 'person'
          ? { scope, userId: person, reason: why, ...days }
          : scope === 'conversation'
            ? { scope, conversationId: conversation, reason: why, ...days }
            : scope === 'number'
              ? { scope, number: number.trim(), reason: why, ...days }
              : scope === 'queue'
                ? { scope, extension: team, reason: why, ...days }
                : { scope: 'company', reason: why, ...days }
      );
      onPlaced();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="smart-place">
      <fieldset className="plain-fieldset">
        <legend>What to hold</legend>
        <div className="radio-cards compact">
          <label className="radio-card">
            <input type="radio" name="hold-scope" checked={scope === 'company'} onChange={() => setScope('company')} />
            <span className="cell-stack">
              <strong>Everything at {companyName}</strong>
              <small className="muted">Every call, recording and voicemail Stitch keeps, and every channel, direct message and text.</small>
            </span>
          </label>
          <label className="radio-card">
            <input type="radio" name="hold-scope" checked={scope === 'person'} onChange={() => setScope('person')} />
            <span className="cell-stack">
              <strong>A person</strong>
              <small className="muted">What they wrote, and every channel and direct message they’re in.</small>
            </span>
          </label>
          {Boolean(t?.conversations.length) && (
            <label className="radio-card">
              <input type="radio" name="hold-scope" checked={scope === 'conversation'} onChange={() => setScope('conversation')} />
              <span className="cell-stack">
                <strong>A channel or text conversation</strong>
                <small className="muted">Everything in it.</small>
              </span>
            </label>
          )}
          <label className="radio-card">
            <input type="radio" name="hold-scope" checked={scope === 'number'} onChange={() => setScope('number')} />
            <span className="cell-stack">
              <strong>A phone number</strong>
              <small className="muted">Calls with it (and their recordings), voicemail from it, and texts with it.</small>
            </span>
          </label>
          {Boolean(t?.teams.length) && (
            <label className="radio-card">
              <input type="radio" name="hold-scope" checked={scope === 'queue'} onChange={() => setScope('queue')} />
              <span className="cell-stack">
                <strong>A ring group or call queue</strong>
                <small className="muted">Its calls, recordings and voicemail, and texts that go to it.</small>
              </span>
            </label>
          )}
        </div>
      </fieldset>
      {scope === 'person' && (
        <SelectField
          label="Person"
          value={person}
          onChange={(e) => setPerson(e.target.value)}
          options={[{ value: '', label: t ? 'Choose someone' : 'Loading…' }, ...(t?.people ?? []).map((p) => ({ value: p.id, label: `${p.name} (${p.email})` }))]}
        />
      )}
      {scope === 'conversation' && (
        <SelectField
          label="Channel or text conversation"
          value={conversation}
          onChange={(e) => setConversation(e.target.value)}
          options={[
            { value: '', label: 'Choose one' },
            ...(t?.conversations ?? []).map((c) => ({ value: c.id, label: c.kind === 'channel' ? `#${c.name ?? ''}` : `Texts with ${formatPhoneNumber(c.number ?? '')}` }))
          ]}
        />
      )}
      {scope === 'number' && (
        <Field label="Phone number" placeholder="(410) 555-0100, or + and the country code" value={number} maxLength={30} onChange={(e) => setNumber(e.target.value)} />
      )}
      {scope === 'queue' && (
        <SelectField
          label="Ring group or call queue"
          value={team}
          onChange={(e) => setTeam(e.target.value)}
          options={[{ value: '', label: 'Choose one' }, ...(t?.teams ?? []).map((x) => ({ value: x.extension, label: `${x.name} (${x.extension})` }))]}
        />
      )}
      <div className="form-grid">
        <Field label="From (optional)" type="date" value={fromDay} onChange={(e) => setFromDay(e.target.value)} hint="Leave both empty to hold every day." />
        <Field label="To (optional)" type="date" value={toDay} onChange={(e) => setToDay(e.target.value)} />
      </div>
      <Field label="What it’s for" placeholder="A case or matter, like Smith v. Acme" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} hint="Shown with the hold, so Stitch support knows when it can be lifted." />
      {error && <Notice tone="crit">{error}</Notice>}
      <div className="settings-actions">
        <Button variant="primary" icon="lock" disabled={busy || !ready} onClick={() => void place()}>
          {busy ? 'Placing…' : 'Place hold'}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
